import {
  CHAT_EMPTY_SIZE,
  DECLINED_GDPR_OFFSET,
  EMAIL_SENDER_ICON_BUFFER_HEIGHT,
  FOOTER_AGG_HEIGHT,
  FOOTER_GDPR_OFFSET,
  FOOTER_GDPR_OFFSET_NO_BRANDING,
  NO_CONVO_CHAT_AGGREGATE_HEIGHT,
  SLIDER_MESSAGE_BUFFER_HEIGHT} from 'constants/magicNumbers'
import useResolvePrivacyPolicy from 'hooks/useResolvePrivacyPolicy'
import { getEmbedConfiguration } from 'modules/embed/selectors'
import { useCallback } from 'react'
import { useSelector } from 'react-redux'
import { WidgetFrames } from 'shared/constants/frames'
import { broadcast } from 'utils/message'

import useResizeObserver from './useResizeObserver'

const useSetPreviewHeight = () => {
  const [previewRefRect, setPreviewRef] = useResizeObserver()

  const hasActiveConversation = useSelector(state =>
    !!state.conversations.activeConversation
  )

  const { showBranding = false } = useSelector(getEmbedConfiguration)

  const { noPolicy } = useResolvePrivacyPolicy()

  const { hasConsent, declinedConsent } = useSelector(state => (
    state.session.gdpr
  ))

  const getHeightOffset = useCallback((): number => {
    let heightOffset = 0
    const noFooter = !showBranding && noPolicy

    if (declinedConsent) {
      heightOffset += DECLINED_GDPR_OFFSET
    }

    if (noFooter) {
      heightOffset += FOOTER_AGG_HEIGHT
    }

    if (!hasConsent) {
      heightOffset += noFooter ? FOOTER_GDPR_OFFSET_NO_BRANDING : FOOTER_GDPR_OFFSET
    }

    return heightOffset
  }, [declinedConsent, hasConsent, noPolicy, showBranding])

  const getPreviewHeight = useCallback((): number => {
    return previewRefRect ? previewRefRect.height : 200
  }, [previewRefRect])

  const setChatHeight = useCallback(async (open: boolean, transitionHeight: boolean = false) => {
    if (hasActiveConversation && hasConsent) { return }

    const heightOffset = getHeightOffset()
    const previewHeight = getPreviewHeight()

    const chatHeight = open
      ? `${previewHeight + NO_CONVO_CHAT_AGGREGATE_HEIGHT - heightOffset}px`
      : '0px'

    // if we are trying to render an interstitial size smaller than the smallest chat frame, skip it
    if (chatHeight === CHAT_EMPTY_SIZE) {
      return
    }

    broadcast({
      topic: 'set-frame-height',
      message: {
        height: chatHeight,
        name: WidgetFrames.CHAT,
        transition: true,
        transitionHeight
      }
    })
  }, [hasActiveConversation, hasConsent, getHeightOffset, getPreviewHeight])

  const setSliderHeight = useCallback(async () => {
    const previewHeight = getPreviewHeight()

    broadcast({
      topic: 'set-frame-height',
      message: {
        height: `${previewHeight + SLIDER_MESSAGE_BUFFER_HEIGHT}px`,
        name: WidgetFrames.SLIDER,
        transition: true,
        transitionHeight: false
      }
    })
  }, [getPreviewHeight])

  const setEmailCaptureHeight = useCallback(async () => {
    const previewHeight = getPreviewHeight()

    broadcast({
      topic: 'set-frame-height',
      message: {
        height: `${previewHeight + EMAIL_SENDER_ICON_BUFFER_HEIGHT}px`,
        name: WidgetFrames.EMAIL_CAPTURE,
        transition: true,
        transitionHeight: false
      }
    })
  }, [getPreviewHeight])

  return {
    setChatHeight,
    setPreviewRef,
    setSliderHeight,
    setEmailCaptureHeight
  }
}

export default useSetPreviewHeight
